import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  MenuIcon, 
  XIcon, 
  SearchIcon,
  HomeIcon, 
  ViewGridIcon, 
  InformationCircleIcon, 
  UserGroupIcon, 
  MailIcon,
  LoginIcon,
  MapIcon,
  AdjustmentsIcon,
  LocationMarkerIcon,
  FilterIcon,
  SortAscendingIcon,
  BellIcon,
  ShoppingCartIcon,
  BookmarkIcon,
  UserIcon,
  ChevronDownIcon
} from '@heroicons/react/outline';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isSearchMenuOpen, setIsSearchMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  
  // Function to handle scroll event
  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY;
      if (offset > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Effet pour fermer les menus lorsqu'on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isSearchMenuOpen && !event.target.closest('.search-menu-container')) {
        setIsSearchMenuOpen(false);
      }
      if (isUserMenuOpen && !event.target.closest('.user-menu-container')) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSearchMenuOpen, isUserMenuOpen]);
  
  // Animation variants
  const navbarVariants = {
    hidden: { opacity: 0, y: -50 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        type: 'spring', 
        stiffness: 100,
        duration: 0.5 
      }
    }
  };
  
  // Mobile menu animation variants
  const mobileMenuVariants = {
    closed: { 
      opacity: 0,
      scale: 0.95,
      transition: {
        duration: 0.2
      }
    },
    open: { 
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.2
      }
    }
  };

  // Logo animation variants
  const logoVariants = {
    initial: { scale: 1 },
    hover: { scale: 1.05 },
    tap: { scale: 0.95 }
  };

  const logoTextVariants = {
    initial: { y: 0 },
    hover: { y: -3 }
  };

  const logoLineVariants = {
    initial: { width: "40px", x: 0 },
    hover: { width: "100%", x: 0 }
  };

  // Animation pour le menu de recherche
  const searchMenuVariants = {
    closed: { 
      opacity: 0,
      y: -20,
      scale: 0.95,
      transition: {
        duration: 0.2
      }
    },
    open: { 
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.2
      }
    }
  };
  
  return (
    <motion.nav 
      className={`fixed w-full z-50 py-4 transition-all duration-300 ${
        scrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-lg text-gray-800 border-b border-gray-200' 
          : 'bg-transparent text-white'
      }`}
      initial="hidden"
      animate="visible"
      variants={navbarVariants}
    >
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          {/* Enhanced Logo */}
          <motion.div 
            className="flex items-center"
            initial="initial"
            whileHover="hover"
            whileTap="tap"
            variants={logoVariants}
          >
            <motion.div 
              className={`p-2 rounded-lg ${
                scrolled 
                  ? 'bg-[#E84E0E]/10' 
                  : 'bg-white/10 backdrop-blur-sm'
              } shadow-lg border border-[#E84E0E]/20 relative overflow-hidden group`}
            >
              {/* Animated background effect */}
              <motion.div 
                className="absolute inset-0 bg-gradient-to-r from-[#E84E0E]/20 to-[#E84E0E]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                animate={{
                  background: [
                    "linear-gradient(to right, rgba(232, 78, 14, 0.1), rgba(232, 78, 14, 0.05))",
                    "linear-gradient(to right, rgba(232, 78, 14, 0.2), rgba(232, 78, 14, 0.1))",
                    "linear-gradient(to right, rgba(232, 78, 14, 0.1), rgba(232, 78, 14, 0.05))"
                  ]
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
              
              <div className="relative z-10">
                <motion.span 
                  className="text-2xl font-bold flex items-center"
                  variants={logoTextVariants}
                >
                  <span className={scrolled ? "text-gray-800" : "text-white"}>DOOH</span>
                  <motion.span 
                    className="text-[#E84E0E]"
                    animate={{
                      textShadow: [
                        "0 0 0px rgba(232, 78, 14, 0.3)",
                        "0 0 8px rgba(232, 78, 14, 0.6)",
                        "0 0 0px rgba(232, 78, 14, 0.3)"
                      ]
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  >
                    Media
                  </motion.span>
                </motion.span>
                <motion.div 
                  className="h-1 rounded-full bg-[#E84E0E]"
                  variants={logoLineVariants}
                  transition={{
                    type: "spring",
                    stiffness: 300
                  }}
                />
              </div>
              
              {/* Animated glow effect */}
              <motion.div 
                className="absolute -inset-0.5 rounded-lg blur opacity-0 group-hover:opacity-75 transition duration-500"
                style={{ 
                  background: "linear-gradient(45deg, #E84E0E 0%, #ff6a34 50%, #E84E0E 100%)",
                  zIndex: -1 
                }}
                animate={{
                  opacity: [0, 0.4, 0]
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
            </motion.div>
          </motion.div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-5">
            <NavItem text="Accueil" icon={<HomeIcon className="h-5 w-5" />} isActive={true} scrolled={scrolled} />
            <NavItem text="Réseaux" icon={<ViewGridIcon className="h-5 w-5" />} scrolled={scrolled} />
            <NavItem text="Comment ça marche" icon={<InformationCircleIcon className="h-5 w-5" />} scrolled={scrolled} />
            {/* <NavItem text="Partenaires" icon={<UserGroupIcon className="h-5 w-5" />} scrolled={scrolled} /> */}
            {/* <NavItem text="Contact" icon={<MailIcon className="h-5 w-5" />} scrolled={scrolled} />    */}

            
            
            {/* Menu de recherche et filtrage */}
            <div className="relative search-menu-container">
              {/* <motion.button
                onClick={() => setIsSearchMenuOpen(!isSearchMenuOpen)}
                className={`flex items-center px-4 py-2 rounded-lg font-medium ${
                  isSearchMenuOpen 
                    ? 'bg-[#E84E0E] text-white'
                    : scrolled 
                      ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' 
                      : 'bg-white/10 backdrop-blur-sm text-white hover:bg-white/20'
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: 'spring', stiffness: 400 }}
              >
                <SearchIcon className="h-5 w-5 mr-1.5" />
                <span>Rechercher</span>
                <FilterIcon className="h-5 w-5 ml-1.5" />
              </motion.button> */}
              
              {/* Menu déroulant de recherche et filtrage */}
              {/* <AnimatedSearchMenu 
                isOpen={isSearchMenuOpen} 
                scrolled={scrolled}
                variants={searchMenuVariants}
              /> */}
            </div>
            
            {/* User Menu */}
            <div className="relative user-menu-container">
              <motion.button
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className={`flex items-center px-3 py-2 rounded-lg font-medium ${
                  isUserMenuOpen 
                    ? 'bg-[#E84E0E] text-white'
                    : scrolled 
                      ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' 
                      : 'bg-white/10 backdrop-blur-sm text-white hover:bg-white/20'
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: 'spring', stiffness: 400 }}
              >
                <UserIcon className="h-5 w-5 mr-1.5" />
                <span>Mon compte</span>
                <ChevronDownIcon className="h-4 w-4 ml-1.5" />
              </motion.button>
              
              {/* User dropdown menu */}
              {isUserMenuOpen && (
                <motion.div
                  className={`absolute right-0 mt-2 w-56 rounded-xl overflow-hidden shadow-lg ${
                    scrolled ? 'bg-white' : 'bg-gray-900/95 backdrop-blur-lg'
                  } p-2`}
                  initial="closed"
                  animate="open"
                  variants={searchMenuVariants}
                >
                  <div className="py-2 px-3 border-b border-gray-200/20 mb-2">
                    <p className={`font-medium ${scrolled ? 'text-gray-800' : 'text-white'}`}>
                      Mon compte
                    </p>
                    <p className={`text-sm ${scrolled ? 'text-gray-500' : 'text-gray-400'}`}>
                      Connectez-vous pour accéder à toutes les fonctionnalités
                    </p>
                  </div>
                  
                  <div className="space-y-1">
                    <UserMenuItem 
                      icon={<LoginIcon className="h-5 w-5" />}
                      text="Connexion / Inscription" 
                      scrolled={scrolled} 
                      isHighlighted={true}
                    />
                    <UserMenuItem 
                      icon={<ShoppingCartIcon className="h-5 w-5" />}
                      text="Mes réservations" 
                      scrolled={scrolled} 
                    />
                    <UserMenuItem 
                      icon={<BookmarkIcon className="h-5 w-5" />}
                      text="Mes favoris" 
                      scrolled={scrolled} 
                    />
                    <UserMenuItem 
                      icon={<BellIcon className="h-5 w-5" />}
                      text="Notifications" 
                      scrolled={scrolled} 
                    />
                  </div>
                </motion.div>
              )}
            </div>
          </div>
          
          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <motion.button
              onClick={() => setIsOpen(!isOpen)}
              whileTap={{ scale: 0.95 }}
              className={`p-2 rounded-full ${
                scrolled 
                  ? 'bg-gray-100 hover:bg-gray-200 text-gray-800' 
                  : 'bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white'
              }`}
            >
              {isOpen ? (
                <XIcon className="h-6 w-6" />
              ) : (
                <MenuIcon className="h-6 w-6" />
              )}
            </motion.button>
          </div>
        </div>
        
        {/* Mobile Menu */}
        {isOpen && (
          <motion.div 
            className={`mt-4 md:hidden ${
              scrolled 
                ? 'bg-white/95 backdrop-blur-md' 
                : 'bg-[#111827]/95 backdrop-blur-lg'
            } shadow-xl rounded-xl p-4`}
            initial="closed"
            animate="open"
            exit="closed"
            variants={mobileMenuVariants}
          >
            <div className="flex flex-col space-y-4">
              <MobileNavItem 
                text="Accueil" 
                icon={<HomeIcon className="h-5 w-5" />}
                isActive={true} 
                scrolled={scrolled} 
              />
              <MobileNavItem 
                text="Réseaux" 
                icon={<ViewGridIcon className="h-5 w-5" />}
                scrolled={scrolled} 
              />
              <MobileNavItem 
                text="Comment ça marche" 
                icon={<InformationCircleIcon className="h-5 w-5" />}
                scrolled={scrolled} 
              />
              <MobileNavItem 
                text="Partenaires" 
                icon={<UserGroupIcon className="h-5 w-5" />}
                scrolled={scrolled} 
              />
              <MobileNavItem 
                text="Contact" 
                icon={<MailIcon className="h-5 w-5" />}
                scrolled={scrolled} 
              />
              
              {/* Recherche et filtrage mobile */}
              {/* <MobileNavItem 
                text="Recherche & Filtrage" 
                icon={<SearchIcon className="h-5 w-5" />}
                scrolled={scrolled} 
                hasBadge={true}
              /> */}
              <MobileNavItem 
                text="Mon compte" 
                icon={<UserIcon className="h-5 w-5" />}
                scrolled={scrolled} 
              />
              
              <motion.button
                className="bg-[#E84E0E] hover:bg-[#d6480e] text-white px-4 py-2 rounded-full w-full font-medium mt-2 flex items-center justify-center"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <LoginIcon className="h-5 w-5 mr-2" />
                Connexion
              </motion.button>
            </div>
          </motion.div>
        )}
      </div>
    </motion.nav>
  );
};

// Desktop NavItem component with icon
const NavItem = ({ text, icon, isActive = false, scrolled }) => {
  return (
    <motion.a
      href="#"
      className={`font-medium relative overflow-hidden flex items-center ${
        isActive 
          ? 'text-[#E84E0E]' 
          : scrolled 
            ? 'text-gray-700 hover:text-[#E84E0E]' 
            : 'text-white/90 hover:text-white'
      }`}
      whileHover={{ scale: 1.05 }}
      transition={{ type: 'spring', stiffness: 400 }}
    >
      <span className="mr-1.5">{icon}</span>
      <span>{text}</span>
      
      {isActive && (
        <motion.div
          className="h-1 w-full bg-[#E84E0E] absolute -bottom-1 rounded-full"
          layoutId="underline"
          initial={{ opacity: 0.7 }}
          animate={{ 
            opacity: [0.7, 1, 0.7],
            boxShadow: [
              "0 0 0px rgba(232, 78, 14, 0.3)",
              "0 0 8px rgba(232, 78, 14, 0.6)",
              "0 0 0px rgba(232, 78, 14, 0.3)"
            ],
          }}
          transition={{ 
            opacity: {
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            },
            boxShadow: {
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }
          }}
        />
      )}
      
      {/* Hover underline animation for non-active items */}
      {!isActive && (
        <motion.div
          className="h-1 w-0 bg-[#E84E0E]/70 absolute -bottom-1 left-0 rounded-full"
          initial={{ width: 0 }}
          whileHover={{ width: "100%" }}
          transition={{ type: "spring", stiffness: 300 }}
        />
      )}
    </motion.a>
  );
};

// Mobile NavItem component with icon
const MobileNavItem = ({ text, icon, isActive = false, scrolled, hasBadge = false }) => {
  return (
    <motion.a
      href="#"
      className={`px-4 py-2 rounded-lg font-medium ${
        isActive 
          ? 'bg-[#E84E0E]/10 text-[#E84E0E]' 
          : scrolled 
            ? 'text-gray-700 hover:bg-gray-100' 
            : 'text-white/80 hover:bg-white/10 hover:text-white'
      } relative overflow-hidden flex items-center`}
      whileHover={{ x: 5 }}
      transition={{ type: 'spring', stiffness: 400 }}
    >
      {/* Background animation for active item */}
      {isActive && (
        <motion.div
          className="absolute inset-0 bg-[#E84E0E]/10 rounded-lg"
          animate={{ 
            opacity: [0.1, 0.2, 0.1]
          }}
          transition={{ 
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      )}
      
      <span className="relative z-10 flex items-center">
        <span className="mr-3">{icon}</span>
        <span>{text}</span>
        
        {/* Badge "NEW" */}
        {hasBadge && (
          <motion.span
            className="ml-2 px-1.5 py-0.5 text-xs font-bold rounded-full bg-[#E84E0E] text-white"
            animate={{ 
              scale: [1, 1.1, 1],
            }}
            transition={{ 
              duration: 1.5,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            NEW
          </motion.span>
        )}
      </span>
      
      {/* Left accent bar */}
      {isActive && (
        <motion.div
          className="absolute left-0 top-0 bottom-0 w-1 rounded-full bg-[#E84E0E]"
          animate={{ 
            height: ["60%", "90%", "60%"],
            y: ["20%", "5%", "20%"]
          }}
          transition={{ 
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      )}
    </motion.a>
  );
};

// Composant pour les items de menu utilisateur
const UserMenuItem = ({ icon, text, scrolled, isHighlighted = false }) => {
  return (
    <motion.a
      href="#"
      className={`flex items-center px-3 py-2 rounded-lg ${
        isHighlighted
          ? 'bg-[#E84E0E] text-white'
          : scrolled 
            ? 'text-gray-700 hover:bg-gray-100' 
            : 'text-white/80 hover:bg-white/10'
      }`}
      whileHover={{ x: 5 }}
      whileTap={{ scale: 0.98 }}
    >
      <span className="mr-3">{icon}</span>
      <span>{text}</span>
    </motion.a>
  );
};

// Composant de menu de recherche animé
const AnimatedSearchMenu = ({ isOpen, scrolled, variants }) => {
  if (!isOpen) return null;
  
  return (
    <motion.div
      className={`absolute right-0 mt-2 w-96 rounded-xl overflow-hidden shadow-xl ${
        scrolled ? 'bg-white' : 'bg-gray-900/95 backdrop-blur-lg'
      } p-3`}
      initial="closed"
      animate="open"
      variants={variants}
    >
      <div className="mb-3">
        <h3 className={`text-lg font-bold ${scrolled ? 'text-gray-800' : 'text-white'}`}>
          Recherche avancée
        </h3>
        <p className={`text-sm ${scrolled ? 'text-gray-500' : 'text-gray-400'}`}>
          Trouvez les écrans parfaits pour votre campagne
        </p>
      </div>
      
      {/* Barre de recherche */}
      <div className={`flex items-center p-2 rounded-lg mb-3 ${
        scrolled ? 'bg-gray-100' : 'bg-gray-800'
      }`}>
        <SearchIcon className={`h-5 w-5 mr-2 ${scrolled ? 'text-gray-500' : 'text-gray-400'}`} />
        <input 
          type="text" 
          placeholder="Rechercher par nom, lieu, type..." 
          className={`bg-transparent w-full outline-none ${
            scrolled ? 'text-gray-800 placeholder-gray-500' : 'text-white placeholder-gray-400'
          }`}
        />
      </div>
      
      {/* Options de recherche */}
      <div className="space-y-3">
        {/* Recherche par localisation */}
        <div>
          <div className={`flex items-center mb-1 ${scrolled ? 'text-gray-700' : 'text-gray-200'}`}>
            <LocationMarkerIcon className="h-4 w-4 mr-1.5" />
            <span className="font-medium text-sm">Par localisation</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button className={`text-sm p-1.5 rounded text-left ${
              scrolled ? 'hover:bg-gray-100 text-gray-600' : 'hover:bg-gray-800 text-gray-300'
            }`}>
              Douala Centre
            </button>
            <button className={`text-sm p-1.5 rounded text-left ${
              scrolled ? 'hover:bg-gray-100 text-gray-600' : 'hover:bg-gray-800 text-gray-300'
            }`}>
              Akwa
            </button>
            <button className={`text-sm p-1.5 rounded text-left ${
              scrolled ? 'hover:bg-gray-100 text-gray-600' : 'hover:bg-gray-800 text-gray-300'
            }`}>
              Bonanjo
            </button>
            <button className={`text-sm p-1.5 rounded text-left ${
              scrolled ? 'hover:bg-gray-100 text-gray-600' : 'hover:bg-gray-800 text-gray-300'
            }`}>
              Bonapriso
            </button>
          </div>
        </div>
        
        {/* Recherche par type de réseau */}
        <div>
          <div className={`flex items-center mb-1 ${scrolled ? 'text-gray-700' : 'text-gray-200'}`}>
            <ViewGridIcon className="h-4 w-4 mr-1.5" />
            <span className="font-medium text-sm">Par type de réseau</span>
          </div>
          <div className="flex flex-wrap gap-2">
            <button className={`text-sm px-3 py-1 rounded-full ${
              scrolled ? 'bg-[#E84E0E]/10 text-[#E84E0E]' : 'bg-[#E84E0E]/20 text-white'
            }`}>
              Retail
            </button>
            <button className={`text-sm px-3 py-1 rounded-full ${
              scrolled ? 'bg-gray-100 text-gray-600' : 'bg-gray-800 text-gray-300'
            }`}>
              Business
            </button>
            <button className={`text-sm px-3 py-1 rounded-full ${
              scrolled ? 'bg-gray-100 text-gray-600' : 'bg-gray-800 text-gray-300'
            }`}>
              Lifestyle
            </button>
            <button className={`text-sm px-3 py-1 rounded-full ${
              scrolled ? 'bg-gray-100 text-gray-600' : 'bg-gray-800 text-gray-300'
            }`}>
              Education
            </button>
          </div>
        </div>
        
        {/* Options de tri */}
        <div>
          <div className={`flex items-center mb-1 ${scrolled ? 'text-gray-700' : 'text-gray-200'}`}>
            <SortAscendingIcon className="h-4 w-4 mr-1.5" />
            <span className="font-medium text-sm">Trier par</span>
          </div>
          <div className="flex flex-wrap gap-2">
            <button className={`text-sm px-3 py-1 rounded-full ${
              scrolled ? 'bg-[#E84E0E]/10 text-[#E84E0E]' : 'bg-[#E84E0E]/20 text-white'
            }`}>
              Popularité
            </button>
            <button className={`text-sm px-3 py-1 rounded-full ${
              scrolled ? 'bg-gray-100 text-gray-600' : 'bg-gray-800 text-gray-300'
            }`}>
              Prix croissant
            </button>
            <button className={`text-sm px-3 py-1 rounded-full ${
              scrolled ? 'bg-gray-100 text-gray-600' : 'bg-gray-800 text-gray-300'
            }`}>
              Prix décroissant
            </button>
          </div>
        </div>
      </div>
      
      {/* Boutons d'action */}
      <div className="mt-4 flex space-x-3">
        <button className={`flex-1 py-2 text-sm font-medium rounded-lg ${
          scrolled ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' : 'bg-gray-800 text-white hover:bg-gray-700'
        }`}>
          Réinitialiser
        </button>
        <motion.button 
          className="flex-1 py-2 text-sm font-medium rounded-lg bg-[#E84E0E] text-white hover:bg-[#d6480e] flex items-center justify-center"
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
        >
          <MapIcon className="h-4 w-4 mr-1.5" />
          Voir sur la carte
        </motion.button>
      </div>
      
      {/* Lien vers la recherche avancée */}
      <div className="mt-3 text-center">
        <a 
          href="#" 
          className={`text-sm ${scrolled ? 'text-[#E84E0E]' : 'text-[#ff6a34]'} hover:underline`}
        >
          Page de recherche avancée →
        </a>
      </div>
    </motion.div>
  );
};

export default Navbar;